import { createSlice } from '@reduxjs/toolkit';
import { get } from 'lodash';
import { geneAsyncAction } from 'services/actionConfigs';
import { RootState } from 'store';
import { useAppSelector } from 'store/hooks';
import { featureName } from './constants';
import {
  GetTicketListParams,
  GetTicketSummaryParams,
  NewTicket,
  Ticket,
  TicketDetail,
  TicketSearchParams,
  TicketSummary,
  UpdateTicketRequest,
} from './types';
import { ApiStatus, PaginatedList } from 'services/commonTypes';
import moment from 'moment';

const defaultSearchParams: TicketSearchParams = {
  status: undefined,
  startDate: moment().format('MM-DD-YYYY'),
  endDate: moment().format('MM-DD-YYYY'),
  paymentType: undefined,
  last4Num: undefined,
  ticketNumber: undefined,
  page: 1,
  size: 10,
};

// Types
type SliceState = {
  ticketList: {
    status: ApiStatus;
    error?: string;
    data?: PaginatedList<Ticket>;
    searchParams: TicketSearchParams;
  };
  ticketSummary: {
    status: ApiStatus;
    error?: string;
    data?: TicketSummary;
  };
  newTicket: {
    status: ApiStatus;
    error?: string;
    data?: NewTicket;
  };
  ticketDetail: {
    status: ApiStatus;
    error?: string;
    data?: TicketDetail;
  };
  onHoldTickets: {
    status: ApiStatus;
    error?: string;
    data?: Ticket[];
  };
  updateTicket: {
    status: ApiStatus;
    error?: string;
    data?: string;
  };
};

// Actions
const createAsync = geneAsyncAction(featureName);
const api = {
  get: {
    list: createAsync<GetTicketListParams>('getTicketList'),
    summary: createAsync<GetTicketSummaryParams>('getTicketSummary'),
    newTicket: createAsync('getNewTicket'),
    onHold: createAsync('getOnHoldTickets'),
    detail: createAsync<string>('getTicketDetail'),
  },
  post: {
    update: createAsync<UpdateTicketRequest>('updateTicket'),
  },
};

// Selectors
const getState = (state: RootState) => state['ticket'];
const select = (key: keyof SliceState) =>
  useAppSelector((state) => get(getState(state), key));

export const use = {
  ticketList: () => select('ticketList'),
  ticketSummary: () => select('ticketSummary'),
  newTicket: () => select('newTicket'),
  ticketDetail: () => select('ticketDetail'),
  onHoldTickets: () => select('onHoldTickets'),
  updateTicket: () => select('updateTicket'),
};

// Slice
const slice = createSlice({
  name: featureName,
  initialState: {
    ticketList: { status: undefined, searchParams: defaultSearchParams },
    ticketSummary: { status: undefined },
    newTicket: { status: undefined },
    ticketDetail: { status: undefined },
    onHoldTickets: { status: undefined },
    updateTicket: { status: undefined },
  } as SliceState,
  reducers: {},
  extraReducers: (builder) => {
    // Ticket List
    builder
      .addCase(api.get.list.fetch, (state, {payload}) => {
        state.ticketList.status = 'fetching';
        state.ticketList.error = undefined;
        state.ticketList.searchParams = payload;
      })
      .addCase(api.get.list.success, (state, { payload }) => {
        state.ticketList.status = 'success';
        state.ticketList.data = payload;
      })
      .addCase(api.get.list.fail, (state, { payload }) => {
        state.ticketList.status = 'error';
        state.ticketList.error = payload;
      });

    // Ticket Summary
    builder
      .addCase(api.get.summary.fetch, (state) => {
        state.ticketSummary.status = 'fetching';
        state.ticketSummary.error = undefined;
      })
      .addCase(api.get.summary.success, (state, { payload }) => {
        state.ticketSummary.status = 'success';
        state.ticketSummary.data = payload;
      })
      .addCase(api.get.summary.fail, (state, { payload }) => {
        state.ticketSummary.status = 'error';
        state.ticketSummary.error = payload;
      });

    // New Ticket
    builder
      .addCase(api.get.newTicket.fetch, (state) => {
        state.newTicket.status = 'fetching';
        state.newTicket.error = undefined;
      })
      .addCase(api.get.newTicket.success, (state, { payload }) => {
        state.newTicket.status = 'success';
        state.newTicket.data = payload;
      })
      .addCase(api.get.newTicket.fail, (state, { payload }) => {
        state.newTicket.status = 'error';
        state.newTicket.error = payload;
      });

    // Ticket Detail
    builder
      .addCase(api.get.detail.fetch, (state) => {
        state.ticketDetail.status = 'fetching';
        state.ticketDetail.error = undefined;
      })
      .addCase(api.get.detail.success, (state, { payload }) => {
        state.ticketDetail.status = 'success';
        state.ticketDetail.data = payload;
      })
      .addCase(api.get.detail.fail, (state, { payload }) => {
        state.ticketDetail.status = 'error';
        state.ticketDetail.error = payload;
      });

    // On Hold Tickets
    builder
      .addCase(api.get.onHold.fetch, (state) => {
        state.onHoldTickets.status = 'fetching';
        state.onHoldTickets.error = undefined;
      })
      .addCase(api.get.onHold.success, (state, { payload }) => {
        state.onHoldTickets.status = 'success';
        state.onHoldTickets.data = payload;
      })
      .addCase(api.get.onHold.fail, (state, { payload }) => {
        state.onHoldTickets.status = 'error';
        state.onHoldTickets.error = payload;
      });

    // Update Ticket
    builder
      .addCase(api.post.update.fetch, (state) => {
        state.updateTicket.status = 'fetching';
        state.updateTicket.error = undefined;
      })
      .addCase(api.post.update.success, (state, { payload }) => {
        state.updateTicket.status = 'success';
        state.updateTicket.data = payload;
      })
      .addCase(api.post.update.fail, (state, { payload }) => {
        state.updateTicket.status = 'error';
        state.updateTicket.error = payload;
      });
  },
});

export const actions = { ...slice.actions, api };
export const reducer = slice.reducer;
